import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import AddEntryModal from './AddEntryModal';

// Brand editorial type — matches the Welcome screen / onboarding.
const SERIF = '"DM Sans", system-ui, sans-serif';

const WEEK_OPTIONS = [
  { label: '1 Week', weeks: 1 },
  { label: '2 Weeks', weeks: 2 },
  { label: '3 Weeks', weeks: 3 },
];

const METRIC_KEYS = [
  { key: 'breakouts', label: 'Breakouts' },
  { key: 'dryness', label: 'Dryness' },
  { key: 'texture', label: 'Texture' },
  { key: 'redness', label: 'Redness' },
  { key: 'darkSpots', label: 'Dark Spots' },
];

// Badge for routine-change entries
const ROUTINE_SOURCE = 'routine_change';

function ScoreBadge({ score, size = 'md' }) {
  if (score == null) return null;
  return (
    <span className={`inline-flex items-center rounded-full font-medium bg-[#F3D5B5] text-[#2A324B] ${size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-0.5'}`}>
      {score}/10
    </span>
  );
}

function SourceBadge({ source }) {
  if (source === 'chat_analysis') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2A324B]/50 bg-[#F0F0F0] px-2 py-0.5 rounded-full">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        From chat
      </span>
    );
  }
  if (source === 'track_scan') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2A324B]/50 bg-[#F0F0F0] px-2 py-0.5 rounded-full">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        Skin scan
      </span>
    );
  }
  if (source === ROUTINE_SOURCE || source === 'routine_change') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-[#2A324B]/50 bg-[#F0F0F0] px-2 py-0.5 rounded-full">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        Routine
      </span>
    );
  }
  return null;
}

// Parse the AI summary into bullets. The model is asked to put each bullet on
// its own line prefixed with "- ", but in practice it sometimes returns them
// joined on a single line ("- foo. - bar. - baz."). Handle both:
//   1. newline-separated bullet lines
//   2. a single line with multiple " - " inline separators
// Falls back to null (caller renders as prose) when neither shape is detected.
export function parseSummaryBullets(summary) {
  if (!summary || typeof summary !== 'string') return null;

  // Shape 1: each bullet on its own line.
  const lines = summary.split('\n').map(l => l.trim()).filter(Boolean);
  const bulletLines = lines.filter(l => /^[-•*]\s+/.test(l));
  if (bulletLines.length >= 2) {
    return bulletLines.map(l => l.replace(/^[-•*]\s+/, '').trim());
  }

  // Shape 2: inline " - " or " • " separators. We split on the boundary, then
  // strip any leading marker the AI included on the first segment.
  const inline = summary
    .split(/\s+(?=[-•*]\s+(?=[A-Z(]))/)
    .map(s => s.replace(/^[-•*]\s+/, '').trim())
    .filter(Boolean);
  if (inline.length >= 2) return inline;

  return null;
}

function SonmiAssessmentBlock({ summary }) {
  const [expanded, setExpanded] = useState(false);
  const bullets = parseSummaryBullets(summary);

  return (
    <div className="mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="text-xs font-semibold text-[#C8956A] tracking-tight">Son:mi assessment</span>
      </div>
      {bullets ? (
        <ul className="space-y-1.5">
          {(expanded ? bullets : bullets.slice(0, 3)).map((b, i) => (
            <li key={i} className="flex gap-2 text-sm text-[#2A324B]/80 leading-relaxed">
              <span className="text-[#C8956A] flex-shrink-0 mt-0.5">•</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-[#2A324B]/80 leading-relaxed">
          {expanded || summary.length <= 120 ? summary : summary.slice(0, 120) + '…'}
        </p>
      )}
      {((bullets && bullets.length > 3) || (!bullets && summary.length > 120)) && (
        <button onClick={() => setExpanded(e => !e)} className="text-xs text-[#C8956A] mt-1.5 font-medium hover:text-[#A87A52]">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}
    </div>
  );
}

function UserNoteBlock({ note }) {
  return (
    <div className="bg-[#F5F5F5] rounded-xl p-3 mb-3">
      <div className="flex items-center gap-1.5 mb-1.5">
        <svg className="w-3 h-3 text-[#2A324B]/45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <span className="text-xs font-semibold text-[#2A324B]/55 tracking-tight">Your note</span>
      </div>
      <p className="text-sm text-[#2A324B]/75 leading-relaxed">{note}</p>
    </div>
  );
}

// Routine-change entries get a compact, expandable card — collapsed shows a
// count, expanded shows each change. Multiple edits on the same day collapse
// into one entry on the backend (skin_journal_entries.routine_changes array).
function RoutineChangeCard({ entry }) {
  const [expanded, setExpanded] = useState(false);
  const date = new Date(entry.created_at).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
  const changes = Array.isArray(entry.routine_changes) ? entry.routine_changes : [];
  const count = changes.length;

  const eventLabel = (e) => {
    const where = `${e.routine}${e.timeOfDay ? ` (${e.timeOfDay})` : ''} — ${e.step}`;
    switch (e.kind) {
      case 'product_added':
        return { verb: 'Added', detail: `${e.product}${e.brand ? ` · ${e.brand}` : ''}`, where };
      case 'product_removed':
        return { verb: 'Removed', detail: `${e.product}${e.brand ? ` · ${e.brand}` : ''}`, where };
      case 'product_changed':
        return { verb: 'Swapped', detail: `${e.from} → ${e.to}`, where };
      case 'frequency_changed':
        return { verb: 'Frequency', detail: `${e.product}: ${e.from} → ${e.to}`, where };
      case 'step_added': return { verb: 'Added step', detail: e.step, where: e.routine };
      case 'step_removed': return { verb: 'Removed step', detail: e.step, where: e.routine };
      default: return { verb: 'Updated', detail: '', where };
    }
  };

  const verbColor = (kind) => {
    if (kind === 'product_added' || kind === 'step_added') return 'text-emerald-600 bg-emerald-50';
    if (kind === 'product_removed' || kind === 'step_removed') return 'text-rose-600 bg-rose-50';
    return 'text-[#C8956A] bg-[#FDF6EE]';
  };

  return (
    <div className="bg-white rounded-2xl p-4 border border-[#EAE0D4]">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-[#FDF6EE] flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-[#C8956A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-[#2A324B] text-sm">
            Routine {count === 1 ? 'change' : 'changes'}
            {count > 0 && <span className="text-[#2A324B]/45 font-medium ml-1">· {count}</span>}
          </p>
          <p className="text-xs text-[#2A324B]/45">{date}</p>
        </div>
        {count > 0 && (
          <button
            onClick={() => setExpanded(e => !e)}
            className="text-xs font-medium text-[#C8956A] hover:text-[#A87A52] px-2 py-1 rounded-lg hover:bg-[#FDF6EE] transition-colors"
          >
            {expanded ? 'Hide' : 'See details'}
          </button>
        )}
      </div>

      {expanded && count > 0 && (
        <div className="mt-3 pt-3 border-t border-[#F5F5F5] space-y-2">
          {changes.map((c, i) => {
            const { verb, detail, where } = eventLabel(c);
            return (
              <div key={i} className="flex items-start gap-2">
                <span className={`text-[13px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${verbColor(c.kind)} flex-shrink-0 mt-0.5`}>
                  {verb}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2A324B] truncate">{detail}</p>
                  <p className="text-xs text-[#2A324B]/45">{where}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Fallback for legacy entries without routine_changes array */}
      {count === 0 && entry.ai_summary && (
        <p className="text-xs text-[#2A324B]/55 mt-2 leading-relaxed">{entry.ai_summary}</p>
      )}
    </div>
  );
}

function JournalEntryCard({ entry, onEdit }) {
  // Routine-change entries get their own compact card
  if (entry.source === 'routine_change') {
    return <RoutineChangeCard entry={entry} />;
  }

  const date = new Date(entry.created_at).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });

  const metrics = entry.skin_metrics || {};
  const filledMetrics = METRIC_KEYS.filter(m => metrics[m.key] != null && metrics[m.key] > 0);
  const metricRows = [];
  for (let i = 0; i < filledMetrics.length; i += 2) {
    metricRows.push(filledMetrics.slice(i, i + 2));
  }

  const hasUserInput = entry.overall_score != null || filledMetrics.length > 0 || entry.notes;
  const isAIOnly = (entry.source === 'chat_analysis' || entry.source === 'track_scan') && !hasUserInput;

  return (
    <div className="bg-white rounded-2xl p-4 border border-[#EAE0D4]">
      {/* Header row */}
      <div className="flex items-center gap-2 mb-3">
        <span className="flex-1" style={{ fontFamily: SERIF, fontWeight: 500, fontSize: 21, color: '#2A1212' }}>{date}</span>
        <ScoreBadge score={entry.overall_score} />
        <SourceBadge source={entry.source} />
        {!entry._isMock && (
          <button onClick={() => onEdit(entry)} className="p-1.5 hover:bg-[#F5F5F5] rounded-lg transition-colors">
            <svg className="w-4 h-4 text-[#2A324B]/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>

      {/* Photos */}
      {entry.photoUrls?.length > 0 && (
        <div className="flex gap-2 mb-3">
          {entry.photoUrls.slice(0, 2).map((url, i) => (
            <img
              key={i}
              src={url}
              alt=""
              className="rounded-xl object-cover"
              style={{ width: entry.photoUrls.length === 1 ? '100%' : '47%', aspectRatio: '1' }}
            />
          ))}
        </div>
      )}

      {/* AI summary */}
      {entry.ai_summary && <SonmiAssessmentBlock summary={entry.ai_summary} />}

      {/* User note — only render when distinct from the AI assessment (the AI-fill
          path in AddEntryModal seeds notes from ai_summary; the dedup keeps the
          journal card from showing the same paragraph twice) */}
      {entry.notes && entry.notes !== entry.ai_summary && (
        <UserNoteBlock note={entry.notes} />
      )}

      {/* Metrics grid */}
      {metricRows.length > 0 && (
        <div className="space-y-2 mb-3">
          {metricRows.map((row, ri) => (
            <div key={ri} className="grid grid-cols-2 gap-2">
              {row.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between bg-[#F5F5F5] rounded-xl px-3 py-2">
                  <span className="text-sm text-[#2A324B]/60">{label}</span>
                  <span className="text-sm font-semibold text-[#2A324B]">{metrics[key]}</span>
                </div>
              ))}
              {row.length === 1 && <div />}
            </div>
          ))}
        </div>
      )}

      {/* CTA for AI-only entries: encourage user to add their own rating */}
      {isAIOnly && (
        <button
          onClick={() => !entry._isMock && onEdit(entry)}
          className="w-full flex items-center justify-center gap-2 py-2.5 border border-dashed border-[#F3D5B5] rounded-xl text-sm text-[#2A324B]/50 hover:bg-[#FDF8F4] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add your own rating
        </button>
      )}
    </div>
  );
}

function BeforeAfterCard({ entries }) {
  const before = entries[entries.length - 1];
  const after = entries[0];
  if (!before || !after || before.id === after.id) return null;

  const improvement = (after.overall_score != null && before.overall_score != null)
    ? after.overall_score - before.overall_score
    : null;

  return (
    <div className="bg-white rounded-2xl p-4 border border-[#EAE0D4]">
      {/* No heading — the on-photo Before/After labels are self-explanatory. */}

      <div className="grid grid-cols-2 gap-3">
        {[{ entry: before, label: 'Before' }, { entry: after, label: 'After' }].map(({ entry, label }) => (
          <div key={label} className="text-center">
            <div className="relative aspect-square rounded-xl overflow-hidden bg-[#F5F5F5] mb-2">
              {entry.photoUrls?.[0] ? (
                <img src={entry.photoUrls[0]} alt={label} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-[#2A324B]/15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
              )}
              <span className="absolute top-2 left-2 bg-black/40 text-white text-xs font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
                {label}
              </span>
            </div>
            <p className="text-xs text-[#2A324B]/50 mb-1.5">
              {new Date(entry.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
            <ScoreBadge score={entry.overall_score} size="sm" />
          </div>
        ))}
      </div>

      {improvement !== null && (
        <>
          <div className={`mt-4 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-sm font-medium ${
            improvement > 0 ? 'bg-[#EEF3EC] text-[#3B6B4A]'
            : improvement < 0 ? 'bg-[#FBF1E6] text-[#9A6B3A]'
            : 'bg-[#F4F1EC] text-[#2A324B]/50'
          }`}>
            <span>{improvement > 0 ? '↗' : improvement < 0 ? '↘' : '→'}</span>
            <span>
              {improvement > 0 ? `+${improvement} since last entry`
                : improvement < 0 ? `${Math.abs(improvement)} lower than last entry`
                : 'Holding steady'}
            </span>
          </div>
          {/* Scores are subjective and photo-dependent — a single dip isn't a
              verdict. Keep the user from reading too much into one number. */}
          <p className="mt-2 text-[13px] text-[#2A324B]/40 text-center leading-relaxed">
            Scores are a rough guide — lighting, angle and time of day all sway them.
            Look at the trend across several entries, not one comparison.
          </p>
        </>
      )}
    </div>
  );
}

export default function SkinTimeline({ userId, hasImageConsent = false, requestImageConsent }) {
  const [weekFilter, setWeekFilter] = useState(1);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const since = new Date();
      since.setDate(since.getDate() - weekFilter * 7);

      const { data, error } = await supabase
        .from('skin_journal_entries')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', since.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;

      const withUrls = await Promise.all(
        (data || []).map(async (entry) => {
          const paths = entry.photo_paths || [];
          const photoUrls = (
            await Promise.all(
              paths.map(async (path) => {
                const { data: signed } = await supabase.storage
                  .from('images')
                  .createSignedUrl(path, 604800); // 7 days; refreshed on every load
                return signed?.signedUrl || null;
              })
            )
          ).filter(Boolean);
          return { ...entry, photoUrls };
        })
      );

      setEntries(withUrls);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [userId, weekFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const allEntries = entries;
  const hasEntries = entries.length > 0;

  return (
    <>
      <div className="space-y-4 pb-6">
        {/* Empty-state explainer — shown only when the user has zero entries */}
        {!loading && !hasEntries && (
          <div className="bg-white rounded-2xl p-4 border border-[#EAE0D4] flex items-start gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#FDF6EE] flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-[#C8956A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-[#2A324B] mb-0.5">Your skin journal</p>
              <p className="text-xs text-[#2A324B]/55 leading-relaxed">
                Your skin analyses and routine changes land here, so you can see how your skin trends over time.
              </p>
            </div>
          </div>
        )}

        {/* Add Entry button */}
        <button
          onClick={() => setShowAddEntry(true)}
          className="w-full py-3.5 rounded-full flex items-center justify-center gap-2 active:opacity-80 transition-opacity"
          style={{ background: '#2A1212', color: '#F4ECE3', fontSize: 13.5, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          New entry
        </button>

        {/* Week filter */}
        <div className="flex gap-2">
          {WEEK_OPTIONS.map(({ label, weeks }) => (
            <button
              key={weeks}
              onClick={() => setWeekFilter(weeks)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                weekFilter === weeks
                  ? 'bg-[#F3D5B5] border-[#F3D5B5] text-[#2A324B]'
                  : 'border-[#E0E0E0] text-[#2A324B]/50 bg-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-[#F3D5B5] border-t-[#2A324B] rounded-full animate-spin" />
          </div>
        ) : (
          hasEntries && (
            <>
              {allEntries.length >= 2 && allEntries.some(e => e.photoUrls?.length > 0) && (
                <BeforeAfterCard entries={allEntries.filter(e => e.photoUrls?.length > 0)} />
              )}

              <div>
                <p className="text-xs font-semibold text-[#2A324B]/35 uppercase tracking-wider mb-3">Journal</p>
                <div className="space-y-3">
                  {allEntries.map(entry => (
                    <JournalEntryCard key={entry.id} entry={entry} onEdit={setEditingEntry} />
                  ))}
                </div>
              </div>
            </>
          )
        )}
      </div>

      {showAddEntry && (
        <AddEntryModal
          userId={userId}
          onClose={() => setShowAddEntry(false)}
          onSaved={() => { setShowAddEntry(false); fetchEntries(); }}
          hasImageConsent={hasImageConsent}
          requestImageConsent={requestImageConsent}
        />
      )}

      {editingEntry && (
        <AddEntryModal
          userId={userId}
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={() => { setEditingEntry(null); fetchEntries(); }}
          hasImageConsent={hasImageConsent}
          requestImageConsent={requestImageConsent}
        />
      )}
    </>
  );
}
